/**
 * Layer 1: Tenant Config Check
 * 
 * Code-level whitelist that ensures experiments can only run on tenants
 * that have explicitly enabled them in their configuration.
 * 
 * This is the first line of defense against misconfigured experiments.
 */

import { AllowedExperimentKey, TenantId } from '@tenants/types';
import { getTenantById } from '@tenants';
import { ExperimentObservabilityService } from 'observability/services/experiment-observability';
import { ExperimentContext } from 'observability/constants/experiment-events';
import type { CircuitBreakerResult } from './types';
import { CircuitBreakerLayer } from './types';

/**
 * Check if experiment is enabled in tenant configuration.
 * 
 * This is the first circuit breaker layer that validates experiments
 * against the tenant's explicit whitelist in their configuration.
 * 
 * @param experimentKey - The experiment to check
 * @param tenantId - The tenant ID to check against
 * @param context - Whether this is SSR or client-side evaluation
 * @returns CircuitBreakerResult if blocked, null if allowed to continue to next layer
 */
export function checkTenantConfig(
  experimentKey: AllowedExperimentKey,
  tenantId: TenantId | undefined,
  context: ExperimentContext
): CircuitBreakerResult | null {
  const tenant = tenantId ? getTenantById(tenantId) : null;
  const experimentConfig = tenant?.features.Experiments?.[experimentKey];
  const isEnabledForTenant = experimentConfig?.enabled ?? false;

  if (!isEnabledForTenant) {
    // Track the block
    ExperimentObservabilityService.trackTenantConfigBlocked({
      experimentKey,
      context,
      tenantId,
      enabledExperiments: tenant?.features.Experiments 
        ? Object.keys(tenant.features.Experiments).filter(
          k => tenant.features.Experiments?.[k as AllowedExperimentKey]?.enabled
        ) as AllowedExperimentKey[]
        : [],
    });

    // Return blocked result
    return {
      allowed: false,
      variationId: 0,
      layer: CircuitBreakerLayer.TENANT_CONFIG,
      reason: 'Blocked by tenant config (not in features.Experiments)',
      isEnabledForTenant: false,
    };
  }

  // Allowed - continue to next layer
  return null;
}

/**
 * Check if experiment is enabled for a tenant (without tracking).
 * 
 * This is a lightweight utility function used by client-side code
 * to check tenant config without triggering observability tracking.
 * 
 * @param experimentKey - The experiment to check
 * @param tenantId - The tenant ID to check against
 * @returns true if experiment is enabled in tenant config, false otherwise
 */
export function isExperimentEnabledForTenant(
  experimentKey: AllowedExperimentKey,
  tenantId: TenantId
): boolean {
  const tenant = getTenantById(tenantId);
  const experimentConfig = tenant.features.Experiments?.[experimentKey];
  return experimentConfig?.enabled ?? false;
}
